import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './faceRecognition.css';
import BASE_URL from '../../config';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
} from '@mui/material';


const FACE_REC_API_URL = 'https://face.mavistech.cloud'; 
const DEFAULT_UID_FALLBACK = "";

function FaceRecognition() {
   const videoRef = useRef(null);
       const canvasRef = useRef(null);
       const audioPlayerRef = useRef(null);
   
       const [currentMode, setCurrentMode] = useState('register');
       const [uid, setUid] = useState('');
       const [personName, setPersonName] = useState('');
       const [status, setStatus] = useState({ message: 'Please select mode, enter details, and start camera.', type: 'info' });
       const [currentStream, setCurrentStream] = useState(null);
       const [isCameraOn, setIsCameraOn] = useState(false);
       const [isCapturing, setIsCapturing] = useState(false);
   
       const [resultsData, setResultsData] = useState({
           headers: null, body: null, contentType: null, isError: false
       });
       const [audioSrc, setAudioSrc] = useState(null);
       const [audioObjectURL, setAudioObjectURL] = useState(null);
   
       const registrationSteps = ['Center', 'Look Left', 'Look Right'];
       const [currentStepIndex, setCurrentStepIndex] = useState(0);
       const [registrationProgress, setRegistrationProgress] = useState({});
   
       const [managedFaces, setManagedFaces] = useState([]);
       const [managementStatus, setManagementStatus] = useState({ message: '', type: 'info', visible: false });
       const [isLoadingManagedFaces, setIsLoadingManagedFaces] = useState(false);

        const token = localStorage.getItem('jwt_token');
        const [devices, setDevices] = useState([]);
        const [selectedUid, setSelectedUid] = useState('');
        const [loadingDevices, setLoadingDevices] = useState(true);

        // Wizard step state
        const [currentWizardStep, setCurrentWizardStep] = useState(1);

        // Pose notification state
        const [poseNotification, setPoseNotification] = useState({ show: false, text: '', icon: '' });

        const getWizardSteps = () => {
            if (currentMode === 'register') {
                return [
                    { number: 1, title: 'Select Device', icon: '1' },
                    { number: 2, title: 'Enter Name', icon: '2' },
                    { number: 3, title: 'Capture Poses', icon: '3' },
                    { number: 4, title: 'Complete', icon: '4' }
                ];
            } else {
                return [
                    { number: 1, title: 'Select Device', icon: '1' },
                    { number: 2, title: 'Manage Faces', icon: '2' }
                ];
            }
        };

useEffect(() => {
  const fetchDevices = async () => {
    try {
        
      const response = await axios.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDevices(response.data);
      console.log("Devices fetched:", response.data);
      // Automatically select first device if only one device is returned
      if (response.data.length === 1) {
        setSelectedUid(response.data[0].uid);
      }
      setUid(response.data[0].uid); 
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  fetchDevices();
}, [token]);

       const updateStatus = (message, type = 'info', target = 'main') => {
           if (target === 'main') {
               setStatus({ message, type });
           } else if (target === 'management') {
               setManagementStatus({ message, type, visible: true });
           }
       };
       const hideManagementStatus = () => setManagementStatus(prev => ({ ...prev, visible: false }));
   
       const clearResultsDisplay = () => {
           setResultsData({ headers: null, body: null, contentType: null, isError: false });
           if (audioObjectURL) {
               URL.revokeObjectURL(audioObjectURL);
               setAudioObjectURL(null);
           }
           setAudioSrc(null);
       };
   
       const processAndDisplayData = (responseHeaders, responseBody, responseContentType, isError = false) => {
           clearResultsDisplay();
           const relayedHeaders = {
               responseType: responseHeaders['x-response-type'] || 'N/A', // Key names from API docs
               resultCode: responseHeaders['result'] || 'N/A',
               originalText: responseHeaders['x-response-text'] || 'N/A',
           };
   
           if (responseContentType && responseContentType.includes('audio/x-raw') && responseBody instanceof Blob) {
               const audioUrl = URL.createObjectURL(responseBody);
               setAudioSrc(audioUrl);
               setAudioObjectURL(audioUrl);
               updateStatus('Audio received. Press play on the audio player.', 'info');
           } else if (responseContentType && responseContentType.includes('application/json')) {
               // body is already object
           } else if (responseContentType && responseContentType.includes('text/plain')) {
               // body is text
           } else if (responseBody instanceof Blob) {
               responseBody.text().then(text => {
                    setResultsData({ headers: relayedHeaders, body: text, contentType: responseContentType || 'text/plain', isError });
               }).catch(e => {
                   console.error("Error reading blob as text:", e);
                   setResultsData({ headers: relayedHeaders, body: "Could not read blob content.", contentType: responseContentType, isError });
               });
               return;
           }
           setResultsData({ headers: relayedHeaders, body: responseBody, contentType: responseContentType, isError });
       };
   
       const handleModeChange = (newMode) => {
           setCurrentMode(newMode);
           clearResultsDisplay();
           hideManagementStatus();
           setManagedFaces([]);
           stopCamera();
           setCurrentWizardStep(1); // Reset to first step
           if (newMode === 'register') {
               updateStatus('', 'info');
               setCurrentStepIndex(0);
               setRegistrationProgress({});
               setPersonName('');
           } else if (newMode === 'detect') {
               updateStatus('Select Device, then Start Camera for Detection.', 'info');
           } else if (newMode === 'manage') {
               updateStatus('', 'info');
           }
       };

       const canProceedToNextStep = () => {
           if (currentMode === 'register') {
               if (currentWizardStep === 1) return selectedUid !== '';
               if (currentWizardStep === 2) return personName.trim() !== '';
               if (currentWizardStep === 3) return currentStepIndex >= registrationSteps.length;
           } else if (currentMode === 'manage') {
               if (currentWizardStep === 1) return selectedUid !== '';
           }
           return false;
       };

       const handleNextStep = () => {
           if (canProceedToNextStep()) {
               const newStep = currentWizardStep + 1;
               setCurrentWizardStep(newStep);

               if (currentMode === 'register' && currentWizardStep === 2) {
                   stopCamera(); // Stop camera when moving to completion step
               }

               // Auto-load faces when entering manage faces step
               if (currentMode === 'manage' && newStep === 2) {
                   listManagedFaces();
               }
           }
       };

       const handlePreviousStep = () => {
           if (currentWizardStep > 1) {
               setCurrentWizardStep(prev => prev - 1);
               stopCamera();
           }
       };
   
       const showPoseNotification = (pose) => {
           const poseIcons = {
               'Center': '●',
               'Look Left': '←',
               'Look Right': '→'
           };

           setPoseNotification({
               show: true,
               text: pose,
               icon: poseIcons[pose] || '○'
           });

           setTimeout(() => {
               setPoseNotification({ show: false, text: '', icon: '' });
           }, 2500);
       };

       const startCamera = async () => {
           if (currentStream) stopCamera();
           updateStatus('Starting camera...', 'info');
           try {
               const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" }, audio: false });
               if (videoRef.current) videoRef.current.srcObject = stream;
               setCurrentStream(stream);
               setIsCameraOn(true);
               if (currentMode === 'register') {
                   updateStatus(`Camera started. Pose: ${registrationSteps[currentStepIndex]}. Click Capture.`, 'info');
                   showPoseNotification(registrationSteps[currentStepIndex]);
               }
               else if (currentMode === 'detect') updateStatus('Camera started. Click Capture to detect faces.', 'info');
           } catch (err) {
               updateStatus(`Error accessing camera: ${err.name}. Check permissions.`, 'error');
               setIsCameraOn(false);
           }
       };
   
       const stopCamera = () => {
           if (currentStream) currentStream.getTracks().forEach(track => track.stop());
           if (videoRef.current) videoRef.current.srcObject = null;
           setCurrentStream(null);
           setIsCameraOn(false);
           if (status.type !== 'error' && managementStatus.type !== 'error' && currentMode !== 'manage') {
               updateStatus('Camera stopped.', 'info');
           }
       };
   
       const captureImageToBlob = () => {
           return new Promise((resolve, reject) => {
               if (!videoRef.current || !canvasRef.current || !isCameraOn) {
                   reject(new Error("Camera or canvas not ready.")); return;
               }
               const video = videoRef.current; const canvas = canvasRef.current;
               const context = canvas.getContext('2d');
               canvas.width = video.videoWidth; canvas.height = video.videoHeight;
               context.drawImage(video, 0, 0, canvas.width, canvas.height);
               canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Failed to create blob.")), 'image/jpeg', 0.9);
           });
       };
   
       const handleCapture = async () => {
           setIsCapturing(true);
           updateStatus('Capturing and processing...', 'info');
           clearResultsDisplay();
   
           let imageBlob;
           try {
               imageBlob = await captureImageToBlob();
           } catch (error) {
               updateStatus(`Failed to capture image: ${error.message}`, 'error');
               setIsCapturing(false); return;
           }
   
           const trimmedUid = uid.trim();
           if (!trimmedUid) {
               updateStatus('UID (Authentication header) cannot be empty.', 'error');
               setIsCapturing(false); return;
           }
   
           const commonHeaders = { 'Authentication': trimmedUid };
           const formData = new FormData();
           // For /recognize (multipart), image is a field. For /recognize_binary, it's the body.
           // We'll stick to /recognize (multipart) for browser clients as it's more standard than raw binary body.
           formData.append('image', imageBlob, `capture-${Date.now()}.jpg`);
   
           let targetUrl = '';
           let requestConfig = {};
           const isRegistration = currentMode === 'register';
   
           if (isRegistration) {
               const trimmedPersonName = personName.trim();
               if (!trimmedPersonName) {
                   updateStatus('Person Name cannot be empty for registration.', 'error');
                   setIsCapturing(false); return;
               }
               formData.append('name', trimmedPersonName); // 'name' field for /register
               targetUrl = `${FACE_REC_API_URL}/register`;
               requestConfig = {
                   headers: commonHeaders, // Axios merges FormData's Content-Type with this
                   responseType: 'json', // /register returns JSON
               };
               updateStatus(`Registering face for ${trimmedPersonName} (Step: ${registrationSteps[currentStepIndex]})...`, 'info');
           } else { // detect mode - using /recognize (multipart)
               targetUrl = `${FACE_REC_API_URL}/recognize`;
               requestConfig = {
                   headers: commonHeaders,
                   responseType: 'blob', // /recognize can return audio (blob) or text
               };
               updateStatus(`Sending image for recognition...`, 'info');
           }
   
           try {
               const response = await axios.post(targetUrl, formData, requestConfig);
               const responseContentType = response.headers['content-type'];
               processAndDisplayData(response.headers, response.data, responseContentType);
   
               if (isRegistration) {
                   const message = response.data?.message || 'Success';
                   setRegistrationProgress(prev => ({ ...prev, [registrationSteps[currentStepIndex]]: { success: true, message } }));
                   const nextStep = currentStepIndex + 1;
                   if (nextStep < registrationSteps.length) {
                       setCurrentStepIndex(nextStep);
                       updateStatus(`Success! Next pose: ${registrationSteps[nextStep]}. Click Capture.`, 'success');
                       // Show notification for next pose
                       showPoseNotification(registrationSteps[nextStep]);
                   } else {
                       updateStatus('All poses captured successfully! Moving to completion...', 'success');
                       // Automatically move to completion step after a brief delay
                       setTimeout(() => {
                           stopCamera();
                           setCurrentWizardStep(4);
                       }, 1500);
                   }
               } else { // Detection
                   const xResponseType = response.headers['x-response-type'];
                   if (xResponseType?.toLowerCase().includes('audio')) {
                       updateStatus('Detection complete. Audio response received.', 'success');
                   } else {
                       updateStatus('Detection complete. Text/JSON response received.', 'success');
                   }
               }
           } catch (error) {
               console.error('API Call Error:', error);
               let errorMsg = error.response?.data?.error || error.message || "An error occurred.";
               let errorDetails = error.response?.data?.details || '';
               let errorBodyForDisplay = error.response?.data || { error: errorMsg, details: errorDetails };
               let errorHeaders = error.response?.headers || {};
               let errorContentType = error.response?.headers['content-type'] || 'application/json';
   
               updateStatus(`${errorMsg}${errorDetails ? ` - ${errorDetails}` : ''}`, 'error');
               processAndDisplayData(errorHeaders, errorBodyForDisplay, errorContentType, true);
   
               if (isRegistration) {
                   setRegistrationProgress(prev => ({ ...prev, [registrationSteps[currentStepIndex]]: { success: false, message: `${errorMsg} ${errorDetails}` } }));
                   updateStatus(`Registration Step Failed: ${errorMsg}. Try capturing again.`, 'error');
               }
           } finally {
               setIsCapturing(false);
           }
       };
   
       const listManagedFaces = async () => {
           const trimmedUid = uid.trim();
           if (!trimmedUid) {
               updateStatus('UID (Authentication header) cannot be empty.', 'error', 'management'); return;
           }
           setIsLoadingManagedFaces(true);
           updateStatus('Fetching face list...', 'info', 'management');
           setManagedFaces([]);
   
           try {
               const response = await axios.get(`${FACE_REC_API_URL}/faces/list`, {
                   headers: { 'Authentication': trimmedUid }
               });
               const rawFaces = response.data?.registered_face_entries || response.data?.registered_persons || [];
               if (rawFaces.length === 0) {
                   updateStatus(response.data?.message || 'No faces registered for this UID.', 'success', 'management');
                   setManagedFaces([{ name: 'No faces registered for this UID.', count: 0, isPlaceholder: true }]);
                   return;
               }
               const facesByName = rawFaces.reduce((acc, face) => {
                   const name = face.name || "Unnamed";
                   if (!acc[name]) acc[name] = { count: 0, ids: [] };
                   acc[name].count++;
                   if (face.id) acc[name].ids.push(face.id);
                   return acc;
               }, {});
               const groupedFaces = Object.keys(facesByName).map(name => ({ name, count: facesByName[name].count, ids: facesByName[name].ids }));
               setManagedFaces(groupedFaces);
               updateStatus(response.data?.message || 'Faces listed successfully.', 'success', 'management');
           } catch (error) {
               console.error('Error listing faces:', error);
               const errorMsg = error.response?.data?.error || error.message;
               updateStatus(`Error listing faces: ${errorMsg}`, 'error', 'management');
               setManagedFaces([]);
           } finally {
               setIsLoadingManagedFaces(false);
           }
       };
   
       const handleDeleteAllFacesByName = async (personNameToDelete) => {
           const trimmedUid = uid.trim();
           if (!trimmedUid) {
               updateStatus('UID (Authentication header) cannot be empty.', 'error', 'management'); return;
           }
           const faceGroup = managedFaces.find(f => f.name === personNameToDelete);
           const idsToDelete = faceGroup ? faceGroup.ids : [];
           const entryCount = faceGroup ? faceGroup.count : 'multiple';
   
           if (idsToDelete.length === 0) {
               updateStatus(`No entries found for name "${personNameToDelete}" to delete.`, 'info', 'management');
               return;
           }
           if (!window.confirm(`Are you sure you want to delete ${entryCount} entries for name: "${personNameToDelete}"?`)) return;
   
           updateStatus(`Deleting entries for name: "${personNameToDelete}"...`, 'info', 'management');
           let deletedCount = 0;
           let failedDeletionsInfo = [];
   
           for (const faceId of idsToDelete) {
               try {
                   await axios.delete(`${FACE_REC_API_URL}/faces/delete/${faceId}`, {
                       headers: { 'Authentication': trimmedUid }
                   });
                   deletedCount++;
               } catch (error) {
                   console.error(`Failed to delete face ID ${faceId}:`, error);
                   failedDeletionsInfo.push({ id: faceId, error: error.response?.data?.error || error.message });
               }
           }
   
           if (failedDeletionsInfo.length === 0) {
               updateStatus(`Successfully deleted ${deletedCount} entries for name "${personNameToDelete}".`, 'success', 'management');
           } else {
               updateStatus(`Deleted ${deletedCount} entries. Failed to delete ${failedDeletionsInfo.length} entries for "${personNameToDelete}". Check console.`, 'error', 'management');
               console.error("Failed deletions:", failedDeletionsInfo);
           }
           listManagedFaces(); // Refresh list
       };
   
       useEffect(() => { return () => { stopCamera(); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
       useEffect(() => { return () => { if (audioObjectURL) URL.revokeObjectURL(audioObjectURL); }; }, [audioObjectURL]);
       useEffect(() => { handleModeChange(currentMode); }, []); // eslint-disable-line react-hooks/exhaustive-deps
   
       const isCaptureButtonDisabled = !isCameraOn || isCapturing || (currentMode === 'register' && currentStepIndex >= registrationSteps.length);
   
       const renderResultsContent = () => {
           if (!resultsData.body && !audioSrc) return null;
           const { body, contentType } = resultsData;
           if (audioSrc) {
               return (<div id="audioContentResults"><h3>Audio Content:</h3><audio ref={audioPlayerRef} src={audioSrc} controls /><p><small>Raw PCM might need conversion.</small></p></div>);
           }
           if (contentType?.includes('application/json')) return (<div id="jsonContentResults"><h3>JSON Content:</h3><pre>{JSON.stringify(body, null, 2)}</pre></div>);
           if (contentType?.includes('text/plain')) return (<div id="textContentResults"><h3>Text Content:</h3><pre>{typeof body === 'string' ? body : JSON.stringify(body)}</pre></div>);
           if (typeof body === 'string') return (<div id="textContentResults"><h3>Content (Type: {contentType||'N/A'}):</h3><pre>{body.substring(0,1000)}</pre></div>);
           if (body) return (<div id="jsonContentResults"><h3>Raw Content (Type: {contentType||'N/A'}):</h3><pre>{JSON.stringify(body, null, 2)}</pre></div>);
           return <p>No displayable content.</p>;
       };

       const wizardSteps = getWizardSteps();

       return (
           <div className="container-face-recognition">
               <h1>Face Recognition</h1>
               <p className="page-description">
                   Follow the steps below to register faces or manage existing registrations.
               </p>

               {/* Step Indicator */}
               <div className="wizard-steps">
                   {wizardSteps.map((step, index) => (
                       <div
                           key={step.number}
                           className={`wizard-step ${currentWizardStep === step.number ? 'active' : ''} ${currentWizardStep > step.number ? 'completed' : ''}`}
                       >
                           <div className="wizard-step-number">
                               {currentWizardStep > step.number ? 'Done' : step.icon}
                           </div>
                           <div className="wizard-step-title">{step.title}</div>
                           {index < wizardSteps.length - 1 && <div className="wizard-step-line"></div>}
                       </div>
                   ))}
               </div>

               {/* Mode Selection - Only show if at step 1 */}
               {currentWizardStep === 1 && (
                   <div className="wizard-content">
                       <div className="section-header">
                           <span className="section-icon"></span>
                           <h2>Choose Action</h2>
                       </div>

                       <div className="mode-selector-grid">
                           <label className={`mode-option ${currentMode === 'register' ? 'selected' : ''}`}>
                               <input type="radio" name="mode" value="register" checked={currentMode === 'register'} onChange={() => handleModeChange('register')} />
                               <div className="mode-option-content">
                                   <div className="mode-option-title">
                                       Register New Face
                                   </div>
                                   <div className="mode-option-description">
                                       Add a new person by capturing multiple angles of their face
                                   </div>
                               </div>
                           </label>

                           <label className={`mode-option ${currentMode === 'manage' ? 'selected' : ''}`}>
                               <input type="radio" name="mode" value="manage" checked={currentMode === 'manage'} onChange={() => handleModeChange('manage')} />
                               <div className="mode-option-content">
                                   <div className="mode-option-title">
                                       Manage Faces
                                   </div>
                                   <div className="mode-option-description">
                                       View and delete registered faces from your device
                                   </div>
                               </div>
                           </label>
                       </div>


                       <div className="device-selection-section">
                           <div className="section-header">
                               <span className="section-icon"></span>
                               <h2>Select Your Device</h2>
                           </div>
                           <FormControl fullWidth margin="normal">
                               <InputLabel>Select a device</InputLabel>
                               <Select
                                   value={selectedUid}
                                   onChange={(e) => setSelectedUid(e.target.value)}
                                   label="Select a device"
                               >
                                   {devices.map((device) => (
                                       <MenuItem key={device.uid} value={device.uid}>
                                           {device.serial}
                                       </MenuItem>
                                   ))}
                               </Select>
                           </FormControl>
                           <p className="help-text">Tip: Select which smart glasses device you want to use.</p>
                       </div>
                   </div>
               )}
               {/* Step 2: Registration - Person Name */}
               {currentMode === 'register' && currentWizardStep === 2 && (
                   <div className="wizard-content">
                       <div className="controls">
                           <div className="section-header">
                               <span className="section-icon"></span>
                               <h2>Person Information</h2>
                           </div>
                           <div className="input-group-fr">
                               <label htmlFor="personName">Person Name:</label>
                               <input
                                   type="text"
                                   id="personName"
                                   value={personName}
                                   onChange={(e) => setPersonName(e.target.value)}
                                   placeholder="Enter the person's name"
                                   autoFocus
                               />
                           </div>
                           <p className="help-text">Tip: Enter the name of the person you want to register. You'll need to capture 3 different poses in the next step.</p>
                       </div>
                   </div>
               )}

               {/* Step 3: Registration - Camera Capture */}
               {currentMode === 'register' && currentWizardStep === 3 && (
                   <div className="wizard-content">
                       <div className="camera-section">
                       <div className="section-header">
                           <span className="section-icon"></span>
                           <h2>Camera{currentMode === 'register' && currentStepIndex < registrationSteps.length && (
                               <span className="step-indicator">Step {currentStepIndex + 1} of {registrationSteps.length}: {registrationSteps[currentStepIndex]}</span>
                           )}</h2>
                       </div>

                       {currentMode === 'register' && !isCameraOn && (
                           <p className="help-text">
                                Click "Start Camera" to begin. You'll be asked to capture your face in 3 different poses:
                               Center, Look Left, and Look Right.
                           </p>
                       )}

                       <div className="video-fr" style={{ position: 'relative' }}>
                           {/* Pose Notification Overlay */}
                           {poseNotification.show && (
                               <div className="pose-notification">
                                   <div className="pose-notification-icon">{poseNotification.icon}</div>
                                   <div className="pose-notification-text">{poseNotification.text}</div>
                               </div>
                           )}

                           <video ref={videoRef} autoPlay playsInline style={{ display: isCameraOn ? 'block' : 'none', backgroundColor: '#000', width: '100%', maxWidth: '640px' }} />
                           {!isCameraOn && (
                               <div style={{
                                   width: '100%',
                                   maxWidth: '640px',
                                   height: '480px',
                                   backgroundColor: 'rgba(0,0,0,0.3)',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   border: '2px dashed rgba(255, 153, 0, 0.3)',
                                   borderRadius: '8px',
                                   margin: '0 auto'
                               }}>
                                   <p style={{ color: '#6c757d', fontSize: '1.125rem' }}>Camera preview will appear here</p>
                               </div>
                           )}
                       </div>
                       <canvas ref={canvasRef} style={{ display: 'none' }} />

                       <div className="button-group">
                           <button className="button-fr" onClick={startCamera} disabled={isCameraOn}>
                                Start Camera
                           </button>
                           <button className="button-fr" onClick={handleCapture} disabled={isCaptureButtonDisabled}>
                               {isCapturing ? ' Processing...' : ' Capture'}
                           </button>
                           <button className="button-fr secondary" onClick={stopCamera} disabled={!isCameraOn}>
                                Stop Camera
                           </button>
                       </div>
                   </div>

                       {/* Registration Progress */}
                       <div id="registration-progress" className="registration-progress">
                           <div className="section-header">
                               <span className="section-icon"></span>
                               <h2>Registration Progress</h2>
                           </div>
                           <p className="help-text">
                               Tip: Complete all 3 steps to successfully register this person. Each step captures a different angle.
                           </p>
                           <ul id="progressList">
                               {registrationSteps.map((step, index) => {
                                   const progress = registrationProgress[step];
                                   let cn = '';
                                   let icon = '';
                                   let statusText = '';

                                   if (progress) {
                                       if (progress.success) {
                                           cn = 'completed';
                                           icon = '';
                                           statusText = ' - Complete';
                                       } else {
                                           cn = 'failed';
                                           icon = '';
                                           statusText = ` - Failed: ${progress.message || 'Error'}`;
                                       }
                                   } else if (index === currentStepIndex && isCameraOn) {
                                       cn = 'pending';
                                       icon = '>';
                                       statusText = ' - Current Step';
                                   } else if (index === currentStepIndex) {
                                       cn = 'pending';
                                       icon = '>>';
                                       statusText = ' - Next';
                                   }

                                   return (
                                       <li key={step} className={cn}>
                                           <span>{icon} {step}</span>
                                           <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>{statusText}</span>
                                       </li>
                                   );
                               })}
                           </ul>
                       </div>
                   </div>
               )}

               {/* Step 4: Registration Complete */}
               {currentMode === 'register' && currentWizardStep === 4 && (
                   <div className="wizard-content">
                       <div className="completion-section">
                           <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                               <div style={{ fontSize: '4rem', marginBottom: '1rem' }}></div>
                               <h2 style={{ color: '#43b581', fontSize: '1.5rem', marginBottom: '1rem' }}>Registration Complete!</h2>
                               <p style={{ color: '#aab7c4', fontSize: '1.125rem', marginBottom: '2rem' }}>
                                   {personName} has been successfully registered on your device.
                               </p>
                               <button className="button-fr" onClick={() => {
                                   handleModeChange('register');
                                   setCurrentWizardStep(1);
                               }}>
                                   + Register Another Person
                               </button>
                           </div>
                       </div>
                   </div>
               )}

               {/* Step 2: Manage Faces */}
               {currentMode === 'manage' && currentWizardStep === 2 && (
                   <div className="wizard-content">
                       <div className="management-section">
                           <div className="section-header">
                               <span className="section-icon"></span>
                               <h2>Manage Registered Faces</h2>
                           </div>

                           <p className="help-text">
                               Tip: Below are all registered faces on your device. You can delete entries if needed.
                           </p>

                           {isLoadingManagedFaces && (
                               <div style={{ textAlign: 'center', padding: '2rem' }}>
                                   {/* <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>Loading...</div> */}
                                   <p style={{ color: '#aab7c4' }}>Loading faces...</p>
                               </div>
                           )}

                           {managementStatus.visible && (
                               <div className={`status-message-fr api-call ${managementStatus.type}`} style={{marginTop: '15px'}}>{managementStatus.message}</div>
                           )}

                           {!isLoadingManagedFaces && (
                               <ul id="faceList">
                                   {managedFaces.length === 0 && managementStatus.type !== 'error' && (
                                       <li style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                                           <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
                                           <p>No registered faces found on this device</p>
                                       </li>
                                   )}
                                   {managedFaces.map((face, index) => (
                                       <li key={face.name + index}>
                                           {face.isPlaceholder ? (
                                               <span>{face.name}</span>
                                           ) : (
                                               <>
                                                   <span>
                                                       <strong>{face.name}</strong>
                                                       <span style={{ marginLeft: '0.5rem', color: '#6c757d', fontSize: '0.875rem' }}>
                                                           ({face.count} {face.count === 1 ? 'entry' : 'entries'})
                                                       </span>
                                                   </span>
                                                   <button className="delete-button" onClick={() => handleDeleteAllFacesByName(face.name)}>
                                                     Delete 
                                                   </button>
                                               </>
                                           )}
                                       </li>
                                   ))}
                               </ul>
                           )}
                       </div>
                   </div>
               )}

               {/* Status Messages */}
               {status.message && currentWizardStep !== 4 && (
                    <div id="status" className={`status-message-fr ${status.type}`}>{status.message}</div>
               )}

               {/* Error Display */}
               {resultsData.isError && (
                   <div id="results" className="results-area">
                       <div className="section-header">
                           <span className="section-icon">!</span>
                           <h2>Error Details</h2>
                       </div>
                       {renderResultsContent()}
                   </div>
               )}

               {/* Navigation Buttons */}
               <div className="wizard-navigation">
                   {currentWizardStep > 1 && currentWizardStep !== 4 && (
                       <button className="button-fr secondary" onClick={handlePreviousStep}>
                           ← Previous
                       </button>
                   )}
                   {currentWizardStep < wizardSteps.length && currentWizardStep !== wizardSteps.length && currentWizardStep !== 3 && (
                       <button
                           className="button-fr"
                           onClick={handleNextStep}
                           disabled={!canProceedToNextStep()}
                       >
                           Next →
                       </button>
                   )}
                   {currentMode === 'register' && currentWizardStep === 3 && currentStepIndex >= registrationSteps.length && (
                       <button className="button-fr" onClick={handleNextStep}>
                           Complete Registration →
                       </button>
                   )}
               </div>
           </div>
       );
   }

export default FaceRecognition;